import { create } from "zustand";
import type {
  Deployment,
  Environment,
  Project,
  RobotConnection,
  TrainingJob,
  User,
} from "./types";
import * as api from "./api";

/* ------------------------------------------------------------------ */
/*  Store shape                                                        */
/* ------------------------------------------------------------------ */

interface AppState {
  /* auth */
  currentUser: User | null;
  authChecked: boolean; // true once we've tried to bootstrap from a stored token

  /* data */
  projects: Project[];
  currentProject: Project | null;
  environments: Environment[];
  trainingJobs: TrainingJob[];
  deployments: Deployment[];
  robotConnections: RobotConnection[];

  /* ui */
  loading: boolean;
  error: string | null;

  /* actions - auth */
  bootstrapAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName?: string) => Promise<void>;
  logout: () => void;

  /* actions - projects */
  fetchProjects: () => Promise<void>;
  fetchProject: (id: string) => Promise<void>;
  createProject: (name: string, description: string) => Promise<Project>;
  deleteProject: (id: string) => Promise<void>;

  /* actions - environments */
  fetchEnvironments: (projectId: string) => Promise<void>;
  fetchEnvironment: (projectId: string, envId: string) => Promise<Environment>;

  /* actions - training */
  fetchTrainingJobs: (projectId: string) => Promise<void>;
  stopTrainingJob: (projectId: string, jobId: string) => Promise<void>;

  /* actions - deployments */
  fetchDeployments: (projectId: string) => Promise<void>;

  /* actions - robots */
  fetchRobotConnections: () => Promise<void>;

  /* helpers */
  clearError: () => void;
}

/* ------------------------------------------------------------------ */
/*  Store implementation                                               */
/* ------------------------------------------------------------------ */

export const useStore = create<AppState>((set, get) => ({
  currentUser: null,
  authChecked: false,

  projects: [],
  currentProject: null,
  environments: [],
  trainingJobs: [],
  deployments: [],
  robotConnections: [],
  loading: false,
  error: null,

  /* ---- auth ------------------------------------------------------ */

  async bootstrapAuth() {
    // On app boot: if we have a stored token, try to fetch /auth/me to
    // confirm it's still valid. If not, the api 401 interceptor clears it.
    const token = api.getStoredToken();
    if (!token) {
      set({ authChecked: true, currentUser: null });
      return;
    }
    try {
      const user = await api.getMe();
      set({ currentUser: user, authChecked: true });
    } catch {
      set({ currentUser: null, authChecked: true });
    }
  },

  async login(email: string, password: string) {
    set({ loading: true, error: null });
    try {
      const tok = await api.login({ email, password });
      api.setStoredToken(tok.access_token);
      const user = await api.getMe();
      set({ currentUser: user, authChecked: true, loading: false });
    } catch (e) {
      api.clearStoredToken();
      set({ error: (e as Error).message, loading: false });
      throw e;
    }
  },

  async register(email: string, password: string, fullName?: string) {
    set({ loading: true, error: null });
    try {
      await api.register({ email, password, full_name: fullName });
      // Auto-login after successful registration.
      const tok = await api.login({ email, password });
      api.setStoredToken(tok.access_token);
      const user = await api.getMe();
      set({ currentUser: user, authChecked: true, loading: false });
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
      throw e;
    }
  },

  logout() {
    api.clearStoredToken();
    set({
      currentUser: null,
      authChecked: true,
      // Also clear any user-scoped data so the next login starts fresh.
      projects: [],
      currentProject: null,
      environments: [],
      trainingJobs: [],
      deployments: [],
      robotConnections: [],
      error: null,
    });
  },

  /* ---- projects -------------------------------------------------- */

  async fetchProjects() {
    set({ loading: true, error: null });
    try {
      const projects = await api.listProjects();
      set({ projects, loading: false });
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
    }
  },

  async fetchProject(id: string) {
    set({ loading: true, error: null });
    try {
      const project = await api.getProject(id);
      set({ currentProject: project, loading: false });
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
    }
  },

  async createProject(name: string, description: string) {
    set({ loading: true, error: null });
    try {
      const project = await api.createProject({ name, description });
      set((s) => ({
        projects: [...s.projects, project],
        loading: false,
      }));
      return project;
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
      throw e;
    }
  },

  async deleteProject(id: string) {
    set({ loading: true, error: null });
    try {
      await api.deleteProject(id);
      set((s) => ({
        projects: s.projects.filter((p) => p.id !== id),
        currentProject:
          s.currentProject?.id === id ? null : s.currentProject,
        loading: false,
      }));
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
    }
  },

  /* ---- environments ---------------------------------------------- */

  async fetchEnvironments(projectId: string) {
    set({ loading: true, error: null });
    try {
      const environments = await api.listEnvironments(projectId);
      set({ environments, loading: false });
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
    }
  },

  async fetchEnvironment(projectId: string, envId: string) {
    set({ loading: true, error: null });
    try {
      const env = await api.getEnvironment(projectId, envId);
      set((s) => {
        const idx = s.environments.findIndex((e) => e.id === env.id);
        const environments =
          idx >= 0
            ? s.environments.map((e) => (e.id === env.id ? env : e))
            : [...s.environments, env];
        return { environments, loading: false };
      });
      return env;
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
      throw e;
    }
  },

  /* ---- training -------------------------------------------------- */

  async fetchTrainingJobs(projectId: string) {
    set({ loading: true, error: null });
    try {
      const trainingJobs = await api.listTrainingJobs(projectId);
      set({ trainingJobs, loading: false });
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
    }
  },

  async stopTrainingJob(projectId: string, jobId: string) {
    try {
      const updated = await api.stopTraining(projectId, jobId);
      set((s) => ({
        trainingJobs: s.trainingJobs.map((j) =>
          j.id === jobId ? updated : j,
        ),
      }));
    } catch (e) {
      set({ error: (e as Error).message });
    }
  },

  /* ---- deployments ----------------------------------------------- */

  async fetchDeployments(projectId: string) {
    set({ loading: true, error: null });
    try {
      const deployments = await api.listDeployments(projectId);
      set({ deployments, loading: false });
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
    }
  },

  /* ---- robots ---------------------------------------------------- */

  async fetchRobotConnections() {
    try {
      const robotConnections = await api.listRobotConnections();
      set({ robotConnections });
    } catch (e) {
      const state = get();
      if (state.robotConnections.length === 0) {
        set({ error: (e as Error).message });
      }
    }
  },

  /* ---- helpers --------------------------------------------------- */

  clearError() {
    set({ error: null });
  },
}));

// When any API call comes back 401, reset the store's auth state so the
// AuthGate kicks the user back to /login on the next render.
api.setUnauthorizedHandler(() => {
  useStore.setState({
    currentUser: null,
    authChecked: true,
  });
});
